import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ShirtIcon, Plus, Package, AlertCircle, History, Settings, Save, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import api from "../lib/api";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export const VetementsInventory = () => {
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [models, setModels] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [quotasStatut, setQuotasStatut] = useState({ permanent: { quota_annuel: 500 }, saisonnier: { quota_annuel: 300 } });
  const [employees, setEmployees] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustQuantity, setAdjustQuantity] = useState(0);
  const [setQuantityInput, setSetQuantityInput] = useState('');
  const [seuilAlerteInput, setSeuilAlerteInput] = useState(10);
  const [savingQuotas, setSavingQuotas] = useState(false);

  // Distribution form
  // 🆕 [Phase 67] quantite vide par défaut (Phase 65 UX), date_remise = aujourd'hui
  const [distForm, setDistForm] = useState({
    employee_id: "", vetement_id: "", vetement_nom: "", taille: "M", quantite: "",
    date_remise: new Date().toISOString().split('T')[0],
    notes: "", approuve_par: "Admin"
  });

  // 🆕 [Phase 67] Helper : indique si l'article sélectionné a des grandeurs.
  // Default true si l'article n'a pas le champ (rétro-compat).
  const selectedModel = models.find(m => m.id === distForm.vetement_id);
  const articleAGrandeurs = selectedModel ? selectedModel.a_grandeurs !== false : true;

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [invRes, modelsRes, distRes, quotasRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/vetements/inventory`),
        axios.get(`${API_URL}/api/vetements/models`),
        axios.get(`${API_URL}/api/vetements/distributions`),
        axios.get(`${API_URL}/api/vetements/quotas-statut`),
        // 🔧 2026-05-23 — include_inactive: voir historique distribution/quotas
        // des ex-employés (ils ont pu avoir des vêtements distribués).
        api.getEmployees({ include_inactive: true })
      ]);
      const invData = invRes.data;
      const modelsData = modelsRes.data;
      const distData = distRes.data;
      const quotasData = quotasRes.data;

      setInventory(invData.inventory || []);
      setModels(modelsData.models || []);
      setDistributions(distData.distributions || []);
      setEmployees(empRes.employees || []);
      if (quotasData.config) {
        setQuotasStatut(quotasData.config);
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const adjustStock = async () => {
    if (!selectedItem) return;
    const delta = adjustQuantity || 0;
    if (delta === 0) return;
    try {
      const response = await axios.post(`${API_URL}/api/vetements/inventory/adjust`, { vetement_id: selectedItem.vetement_id, taille: selectedItem.taille, quantite: delta });
      if (response.status === 200) {
        toast.success(`✅ Stock ajusté de ${delta > 0 ? '+' : ''}${delta}`);
        setShowAdjustModal(false);
        setAdjustQuantity(0);
        setSetQuantityInput('');
        loadAll();
      } else { toast.error("Erreur lors de l'ajustement"); }
    } catch (error) { toast.error("Erreur lors de l'ajustement"); }
  };

  const handleDistribute = async () => {
    // 🆕 [Phase 67] Validation Phase 65 : quantité > 0 (refuse vide/0)
    const qte = parseInt(distForm.quantite);
    if (!distForm.employee_id || !distForm.vetement_id || !qte || qte <= 0) {
      toast.error("Veuillez remplir tous les champs (quantité > 0)"); return;
    }
    // 🆕 [Phase 67] Si l'article n'a pas de grandeurs, on envoie taille="" (sentinel)
    const model = models.find(m => m.id === distForm.vetement_id);
    const hasGrandeurs = model ? model.a_grandeurs !== false : true;
    const tailleFinal = hasGrandeurs ? distForm.taille : "";

    try {
      const payload = {
        id: crypto.randomUUID(),
        employee_id: distForm.employee_id,
        vetement_id: distForm.vetement_id,
        vetement_nom: distForm.vetement_nom,
        taille: tailleFinal,
        quantite: qte,
        date_distribution: new Date().toISOString(),
        // 🆕 [Phase 67] Date de remise (saisie manuelle, distincte de l'horodatage technique)
        date_remise: distForm.date_remise || new Date().toISOString().split('T')[0],
        approuve_par: distForm.approuve_par || "Admin",
        notes: distForm.notes || null
      };
      const res = await axios.post(`${API_URL}/api/vetements/distribute`, payload);
      if (res.status === 200) {
        toast.success("✅ Distribution enregistrée");
        setShowDistributeModal(false);
        setDistForm({
          employee_id: "", vetement_id: "", vetement_nom: "", taille: "M", quantite: "",
          date_remise: new Date().toISOString().split('T')[0],
          notes: "", approuve_par: "Admin"
        });
        loadAll();
      } else {
        const err = res.data;
        toast.error(err.detail || "Erreur distribution");
      }
    } catch (e) { toast.error("Erreur distribution"); }
  };

  const saveQuotasStatut = async () => {
    setSavingQuotas(true);
    try {
      const res = await axios.put(`${API_URL}/api/vetements/quotas-statut`, quotasStatut);
      if (res.status === 200) { toast.success("✅ Quotas sauvegardés"); }
      else { toast.error("Erreur sauvegarde"); }
    } catch (e) { toast.error("Erreur sauvegarde"); }
    finally { setSavingQuotas(false); }
  };

  // Grouper inventaire par modèle
  const groupedInventory = {};
  inventory.forEach(item => {
    const key = item.vetement_id;
    if (!groupedInventory[key]) groupedInventory[key] = { vetement_nom: item.vetement_nom, vetement_type: item.vetement_type, items: [] };
    groupedInventory[key].items.push(item);
  });

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-4">
      <Tabs defaultValue="inventaire">
        <TabsList>
          <TabsTrigger value="inventaire"><Package className="w-4 h-4 mr-2" />Inventaire</TabsTrigger>
          <TabsTrigger value="historique"><History className="w-4 h-4 mr-2" />Historique</TabsTrigger>
        </TabsList>

        {/* ====== INVENTAIRE ====== */}
        <TabsContent value="inventaire" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <div className="grid grid-cols-3 gap-4 flex-1 mr-4">
              <Card><CardContent className="pt-4">
                <p className="text-xs text-slate-500">Modèles</p>
                <p className="text-2xl font-bold">{models.length}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <p className="text-xs text-slate-500">Total Stock</p>
                <p className="text-2xl font-bold">{inventory.reduce((s, i) => s + (i.quantite_stock || 0), 0)}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <p className="text-xs text-slate-500 text-amber-600">Alertes Stock Bas</p>
                <p className="text-2xl font-bold text-amber-600">{inventory.filter(i => i.quantite_stock <= i.seuil_alerte).length}</p>
              </CardContent></Card>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowDistributeModal(true)} className="bg-green-600 hover:bg-green-700" data-testid="btn-remettre-item">
                <Plus className="w-4 h-4 mr-2" /> Remettre Item
              </Button>
            </div>
          </div>

          {Object.entries(groupedInventory).map(([vetement_id, group]) => (
            <Card key={vetement_id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShirtIcon className="w-5 h-5 text-blue-600" />
                    {group.vetement_nom}
                    <Badge variant="outline">{group.vetement_type}</Badge>
                  </CardTitle>
                  <span className="text-sm text-slate-500">Total: {group.items.reduce((s, i) => s + i.quantite_stock, 0)} unités</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {group.items.map(item => {
                    const isLow = item.quantite_stock <= item.seuil_alerte;
                    return (
                      <div key={`${item.vetement_id}_${item.taille}`}
                        className={`p-3 border rounded-lg text-center cursor-pointer hover:shadow-md transition-shadow ${isLow ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}
                        onClick={() => { setSelectedItem(item); setSeuilAlerteInput(item.seuil_alerte ?? 10); setShowAdjustModal(true); }}>
                        <p className="font-semibold text-slate-900">{item.taille}</p>
                        <p className={`text-2xl font-bold ${isLow ? 'text-amber-600' : 'text-green-600'}`}>{item.quantite_stock}</p>
                        <p className="text-xs text-slate-500">en stock</p>
                        {isLow && <Badge variant="destructive" className="mt-1 text-xs">Bas</Badge>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {Object.keys(groupedInventory).length === 0 && (
            <Card><CardContent className="py-12 text-center text-slate-500">
              <ShirtIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun article en inventaire</p>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ====== HISTORIQUE ====== */}
        <TabsContent value="historique" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Historique des remises de vêtements
                <Badge variant="outline">{distributions.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {distributions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune distribution enregistrée</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-slate-500">
                        <th className="text-left py-2 pr-4">Date</th>
                        <th className="text-left py-2 pr-4">Employé</th>
                        <th className="text-left py-2 pr-4">Vêtement</th>
                        <th className="text-left py-2 pr-4">Taille</th>
                        <th className="text-left py-2 pr-4">Qté</th>
                        <th className="text-left py-2">Approuvé par</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distributions.map((d, idx) => {
                        const emp = employees.find(e => e.id === d.employee_id);
                        const empNom = emp ? `${emp.prenom} ${emp.nom}` : (d.employee_nom || d.employee_id);
                        return (
                          <tr key={d.id || idx} className="border-b hover:bg-slate-50">
                            <td className="py-2 pr-4 text-slate-500">
                              {d.date_distribution ? new Date(d.date_distribution).toLocaleDateString('fr-CA') : '-'}
                            </td>
                            <td className="py-2 pr-4 font-medium">
                              <div className="flex items-center gap-2">
                                <EmployeeAvatar photoUrl={emp?.photo_url} prenom={(empNom || '?').split(' ')[0]} nom={(empNom || '').split(' ').slice(1).join(' ')} size="xs" />
                                {empNom}
                              </div>
                            </td>
                            <td className="py-2 pr-4">{d.vetement_nom}</td>
                            <td className="py-2 pr-4"><Badge variant="outline">{d.taille}</Badge></td>
                            <td className="py-2 pr-4 font-bold">{d.quantite}</td>
                            <td className="py-2 text-slate-500">{d.approuve_par}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Ajustement Stock */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajuster Stock — {selectedItem?.vetement_nom} ({selectedItem?.taille})</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Stock actuel</Label>
              <Input value={selectedItem?.quantite_stock || 0} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>Nouvelle valeur exacte (remplace le stock actuel)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  placeholder="Ex: 10"
                  value={setQuantityInput}
                  onChange={e => setSetQuantityInput(e.target.value)}
                  data-testid="input-set-stock"
                />
                <Button
                  type="button"
                  onClick={async () => {
                    const val = parseInt(setQuantityInput);
                    if (isNaN(val) || val < 0) { toast.error("Entrer un nombre valide"); return; }
                    try {
                      const res = await axios.post(`${API_URL}/api/vetements/inventory/set`, {
                        vetement_id: selectedItem.vetement_id,
                        taille: selectedItem.taille,
                        quantite: val
                      });
                      if (res.status === 200) {
                        toast.success(`✅ Stock défini à ${val}`);
                        setShowAdjustModal(false); setAdjustQuantity(0); setSetQuantityInput(''); loadAll();
                      }
                    } catch { toast.error("Erreur"); }
                  }}
                  className="bg-blue-600"
                  data-testid="btn-set-stock"
                >Définir</Button>
              </div>
            </div>
            <div className="border-t pt-4">
              <Label>Ou ajuster (+réception / -correction)</Label>
              <Input
                type="number"
                value={adjustQuantity}
                onChange={e => setAdjustQuantity(parseInt(e.target.value) || 0)}
                data-testid="input-adjust-stock"
              />
              <p className="text-xs text-slate-500 mt-1">
                Nouveau stock: {(selectedItem?.quantite_stock || 0) + (adjustQuantity || 0)}
              </p>
            </div>
            {/* Seuil d'alerte */}
            <div className="border-t pt-4 bg-amber-50 -mx-6 px-6 pb-2">
              <Label className="flex items-center gap-1 text-amber-700">
                <AlertCircle className="w-3.5 h-3.5" /> Seuil d'alerte stock bas
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  min="0"
                  value={seuilAlerteInput}
                  onChange={e => setSeuilAlerteInput(parseInt(e.target.value) || 0)}
                  data-testid="input-seuil-alerte"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.put(`${API_URL}/api/vetements/inventory/seuil`, {
                        vetement_id: selectedItem.vetement_id,
                        taille: selectedItem.taille,
                        seuil_alerte: seuilAlerteInput
                      });
                      if (res.status === 200) {
                        toast.success(`✅ Seuil d'alerte: ${seuilAlerteInput}`);
                        loadAll();
                      }
                    } catch { toast.error("Erreur"); }
                  }}
                  data-testid="btn-save-seuil"
                >Enregistrer seuil</Button>
              </div>
              <p className="text-xs text-amber-600 mt-1">
                Un email sera envoyé à l'adresse configurée dans "Alertes" lorsque le stock tombe à ce seuil ou en-dessous.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdjustModal(false); setAdjustQuantity(0); }}>Annuler</Button>
            <Button onClick={adjustStock} className="bg-green-600" data-testid="btn-confirm-adjust">Confirmer Ajustement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Remise de vêtement */}
      <Dialog open={showDistributeModal} onOpenChange={setShowDistributeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remettre un item à un employé</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employé *</Label>
              <Select value={distForm.employee_id || undefined} onValueChange={v => {
                const emp = employees.find(e => e.id === v);
                setDistForm(p => ({ ...p, employee_id: v, taille: emp?.taille_vetements || "M" }));
              }}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un employé" /></SelectTrigger>
                <SelectContent>
                  {/* 🆕 [Phase 40 — P1 #3] Tri actifs en premier, badge gris pour inactifs.
                      Permet de remettre des items aux ex-employés (cas oublis). */}
                  {[...employees]
                    .sort((a, b) => {
                      const aActif = a.actif !== false;
                      const bActif = b.actif !== false;
                      if (aActif !== bActif) return aActif ? -1 : 1;
                      return (a.prenom || '').localeCompare(b.prenom || '');
                    })
                    .map(e => (
                    <SelectItem key={e.id} value={e.id} data-testid={`distribute-emp-option-${e.id}`}>
                      <div className="flex items-center justify-between gap-6 w-full">
                        <div className="flex items-center gap-2">
                          <span>{e.prenom} {e.nom}</span>
                          {e.actif === false && (
                            <span
                              className="text-[10px] text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded font-medium"
                              data-testid={`badge-inactif-${e.id}`}
                            >
                              Inactif
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                          {e.taille_vetements || "M"}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item *</Label>
              <Select value={distForm.vetement_id || undefined} onValueChange={v => {
                const m = models.find(m => m.id === v);
                setDistForm(p => ({ ...p, vetement_id: v, vetement_nom: m?.nom || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un item" /></SelectTrigger>
                <SelectContent>
                  {models.map(m => <SelectItem key={m.id} value={m.id}>{m.nom} ({m.type})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* 🆕 [Phase 67] Date de remise */}
            <div>
              <Label>Date de remise *</Label>
              <Input
                type="date"
                value={distForm.date_remise}
                onChange={e => setDistForm(p => ({ ...p, date_remise: e.target.value }))}
                data-testid="dialog-distribute-date-remise"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* 🆕 [Phase 67] Sélecteur Taille masqué si article unisize */}
              {articleAGrandeurs ? (
                <div>
                  <Label>Taille</Label>
                  <Select value={distForm.taille || undefined} onValueChange={v => setDistForm(p => ({ ...p, taille: v }))}>
                    <SelectTrigger data-testid="dialog-distribute-taille"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["XS", "S", "M", "L", "XL", "XXL", "XXXL"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {distForm.employee_id && (() => {
                    const emp = employees.find(e => e.id === distForm.employee_id);
                    return emp?.taille_vetements
                      ? <p className="text-xs text-blue-600 mt-1">Taille préférée : {emp.taille_vetements}</p>
                      : null;
                  })()}
                </div>
              ) : (
                <div className="flex items-end" data-testid="dialog-distribute-no-grandeurs">
                  <p className="text-xs text-slate-500 italic pb-2">📏 Article sans grandeurs</p>
                </div>
              )}
              <div>
                <Label>Quantité *</Label>
                {/* 🆕 [Phase 67 + Phase 65] Champ vide par défaut, placeholder "Qté" */}
                <Input
                  type="number"
                  min="1"
                  value={distForm.quantite}
                  onChange={e => setDistForm(p => ({ ...p, quantite: e.target.value }))}
                  placeholder="Qté"
                  data-testid="dialog-distribute-quantite"
                />
              </div>
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Input value={distForm.notes} onChange={e => setDistForm(p => ({ ...p, notes: e.target.value }))} placeholder="Raison, occasion..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDistributeModal(false)}>Annuler</Button>
            <Button onClick={handleDistribute} className="bg-green-600">Confirmer la remise</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
